/**
 * FIVE LAB — Code101 chapter 1, the whole catalogue, going round.
 *
 * FOUNDER, 2026-08-14: "ch1 too" — after I said this chapter might not want an instrument.
 *
 * ── I WAS HALF WRONG, AND THE HALF THAT WAS RIGHT SHAPED THIS ─────────────────────────────────
 * Chapter 1 IS definitional: most of it is what software is, and a definition does not want a
 * dial. But §4 makes one claim that prose can only assert — "Most software spends most of its time
 * in this state" — and proportion is exactly what a picture carries and a sentence does not. So
 * the instrument is the loop, and the loop is honest about where the time goes: the ring sits in
 * WAIT while the other four flash past, and the share is COMPUTED from the dwells rather than
 * printed as a claim.
 *
 * Everything else on this surface is a worksheet, not a demonstration, because that is what the
 * chapter actually asks for.
 *
 * ── IT WILL NOT DECOMPOSE AN APP FOR YOU ──────────────────────────────────────────────────────
 * §4 names three examples and decomposes none of them, then hands the reader five questions. A lab
 * that filled in "WhatsApp: input is a tap, work is on a far machine…" would take the exercise away
 * and substitute my analysis for the learner's — and my analysis of WhatsApp is not in the module,
 * so it would be the one unsourced thing on the page. The slots start empty and stay the learner's.
 *
 * "If a slot is empty, say so — that is also an answer" is his rule and it is honoured literally:
 * the empty state is offered as a button, not treated as incomplete.
 */

import { useState, useEffect, useRef } from 'react';
import { Value } from '../../../packages/ankr-glass/src/react.jsx';
import { computed, cited, absent } from '../../../packages/ankr-provenance/src/provenance.mjs';
import {
  THINGS, waitShare, WHOLE_CATALOGUE, HIS_EXAMPLES, NOT_IN_THE_FIVE, QUESTIONS,
  EMPTY_IS_AN_ANSWER, A_WORKING_MAP, DOES_NOT_KNOW, MARKETING, TRANSLATE, FOUR_QUESTIONS,
  AI_IS_NEXT, RECIPE, SOURCE_1, SOURCE_4, SOURCE_6,
} from './fivethings.mjs';

const TICK = 150;   // one unit of dwell, in ms — the ring's clock, not a claim about real software

export default function FiveLab() {
  const [running, setRunning] = useState(false);
  const [at, setAt] = useState(4);          // start where software actually sits: waiting
  const [left, setLeft] = useState(THINGS[4].dwell);
  const [slots, setSlots] = useState({});
  const timer = useRef(0);

  useEffect(() => {
    if (!running) return undefined;
    timer.current = setTimeout(() => {
      setLeft((l) => {
        if (l > 1) return l - 1;
        setAt((i) => { const n = (i + 1) % THINGS.length; setLeft(THINGS[n].dwell); return n; });
        return 0;
      });
    }, TICK);
    return () => clearTimeout(timer.current);
  }, [running, at, left]);

  const cur = THINGS[at];
  const share = waitShare();
  const filled = QUESTIONS.filter((q) => (slots[q.id] || '').trim()).length;

  return (
    <div className="g-glass shell-panel lab">
      <div className="g-label">Five lab · the whole catalogue</div>
      <h2 className="g-h2 lab-h">Five things, and mostly waiting</h2>
      <p className="g-body"><Value of={cited(WHOLE_CATALOGUE, SOURCE_4)} /></p>

      {/* ── THE RING ── */}
      <div className="fiv-ring">
        {THINGS.map((t, i) => (
          <div key={t.id} className={`fiv-node ${i === at ? 'fiv-now' : ''} ${t.id === 'wait' ? 'fiv-wait' : ''}`}
            style={{ flexGrow: t.dwell }}>
            <div className="fiv-nhead"><span className="cou-n">{t.n}</span><b>{t.name}</b></div>
            {i === at && <p className="g-body fiv-text">{t.text}</p>}
          </div>
        ))}
      </div>

      <div className="lab-task-head">
        <button className="g-btn g-btn-primary" onClick={() => setRunning((r) => !r)}>
          {running ? 'Pause' : 'Let it run'}
        </button>
        <button className="g-btn" onClick={() => { setRunning(false); setAt(4); setLeft(THINGS[4].dwell); }}>
          Back to waiting
        </button>
      </div>

      <div className="lab-readout">
        <div><span className="g-label">Right now the machine is</span>
          <span className="lab-num">{cur.name.toLowerCase()}</span></div>
        <div><span className="g-label">Share of one cycle spent waiting</span>
          <Value of={computed(`${share}%`)} /></div>
      </div>
      <p className="g-body g-dim fiv-note">
        The wide box is not a design flourish — it is the claim. Each station's width is how long the
        ring rests there, and the share above is computed from those widths, not typed in.
        <Value of={cited('Most software spends most of its time in this state.', SOURCE_4)} />
      </p>

      <div className="fiv-eg">
        {HIS_EXAMPLES.map((e) => <p key={e} className="g-body g-dim"><Value of={cited(e, SOURCE_4)} /></p>)}
        <p className="g-body"><Value of={cited(NOT_IN_THE_FIVE, SOURCE_4)} /></p>
      </div>

      {/* ── THE WORKSHEET. His five questions; the answers are the learner's. ── */}
      <div className="lab-task">
        <div className="g-label">Now do it to something of your own</div>
        <p className="g-body"><Value of={cited(A_WORKING_MAP, SOURCE_4)} /></p>
        <div className="fiv-sheet">
          {QUESTIONS.map((q) => {
            const v = slots[q.id] || '';
            const isEmpty = v === '(nothing — and that is an answer)';
            return (
              <div key={q.id} className="fiv-row">
                <span className="g-label fiv-q">{q.ask}</span>
                <input className="morse-input fiv-in" value={v} placeholder="one sentence"
                  onChange={(e) => setSlots((s) => ({ ...s, [q.id]: e.target.value }))} />
                <button className={`g-btn ${isEmpty ? 'g-btn-primary' : ''}`}
                  onClick={() => setSlots((s) => ({ ...s, [q.id]: isEmpty ? '' : '(nothing — and that is an answer)' }))}>
                  empty
                </button>
              </div>
            );
          })}
        </div>
        <div className="lab-readout">
          <div><span className="g-label">Slots you have answered</span>
            {filled ? <Value of={computed(`${filled} of 5`)} /> : <Value of={absent()} />}</div>
        </div>
        <p className="g-body g-dim"><Value of={cited(EMPTY_IS_AN_ANSWER, SOURCE_4)} /> Nothing here is
          saved, and nothing here is marked — these are your sentences, for your journal.</p>
      </div>

      {/* ── §6 ── */}
      <div className="lab-task">
        <div className="g-label">And the word to distrust</div>
        <p className="g-body"><Value of={cited(DOES_NOT_KNOW, SOURCE_6)} /></p>
        <p className="g-body fiv-marketing"><Value of={cited(MARKETING, SOURCE_6)} /></p>
        <p className="g-body"><Value of={cited(TRANSLATE, SOURCE_6)} /></p>
        <ul className="fai-list">{FOUR_QUESTIONS.map((q) => <li key={q}>{q}</li>)}</ul>
        <p className="g-body g-dim"><Value of={cited(AI_IS_NEXT, SOURCE_6)} /></p>
      </div>

      <p className="g-body fiv-punch"><Value of={cited(RECIPE, SOURCE_1)} /></p>

      <p className="g-body g-dim lab-why">
        This lab will not fill the five slots in for you. The module names three apps — the
        calculator, WhatsApp, the bank app — and decomposes none of them, because the point of §4 is
        that YOU can now do it to anything. An answer typed here by me would be the only unsourced
        sentence on the page, and it would take the exercise away.
        <br />
        The ring's clock is {TICK} ms a unit and is not a claim about real software: a calculator's
        work takes microseconds and its wait can be days. What the widths carry is the ratio the
        module states, not a duration.
      </p>
    </div>
  );
}
